import React, { useCallback, useEffect, useMemo } from 'react';
import { Drawer, Accordion, Icons, Icon, Tooltip } from '@silverfort/sf-components';
import { JIT_MFA_TYPES, SERVICE_ACCOUNTS_ENTITY_IDENTIFIER_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import StatusBadge, { StatusBadgeVariants } from '../components/StatusBadge';
import JitPolicy from './JitPolicy';
import { httpGet, httpPost, httpPut } from '../../../components/utils';
import FencingPolicy from './FencingPolicy';
import { usePrivilegedAccountContext } from '../context/PrivilegedAccountsContext';
import { JIT_DEFAULT_MIN_ENABLE_HOURS, JIT_DEFAULT_MAX_ENABLE_HOURS } from '../consts';
import { formatFencingPolicyForSave } from '../utils/formatters';

const BADGE_OPTIONS = {
    PROTECTED: { title: 'Active', theme: StatusBadgeVariants.Positive },
    EDIT: { title: 'Edit', theme: StatusBadgeVariants.Neutral },
    NOT_PROTECTED: { title: 'Inactive', theme: StatusBadgeVariants.Negative },
    BREAK_GLASS: {
        title: 'Break Glass',
        theme: StatusBadgeVariants.Negative,
        tooltipProps: {
            width: 320,
            title: 'Break glass account',
            description: 'Account selected for emergency use and cannot be protected with a JIT policy'
        }
    },
    MISSING_CONFIG: {
        title: 'Missing Config',
        theme: StatusBadgeVariants.Negative,
        tooltipProps: {
            width: 320,
            title: 'Missing JIT settings',
            description:
                'AD attributes and Break Glass account are required for JIT policy activation. Update the required fields in the JIT Settings tab of the Privileged Access Settings page.'
        }
    }
};

const PaPolicies = ({ account, open, onClose, onSave, onError, isEditor }) => {
    const [jitStatus, setJitStatus] = React.useState();
    const [isBreakGlass, setIsBreakGlass] = React.useState(true);
    const [isBreakGlassDisabled, setIsBreakGlassDisabled] = React.useState(true);
    const [originalJitPolicy, setOriginalJitPolicy] = React.useState();
    const [editedJitPolicy, setEditedJitPolicy] = React.useState();
    const [fencingStatus, setFencingStatus] = React.useState();
    const [originalFencingPolicy, setOriginalFencingPolicy] = React.useState();
    const [editedFencingPolicy, setEditedFencingPolicy] = React.useState();

    const { jitConfig } = usePrivilegedAccountContext();

    useEffect(() => {
        if ((!jitConfig?.isJitConfigSet || isBreakGlass) && (!originalJitPolicy || !editedJitPolicy?.protected)) {
            setIsBreakGlassDisabled(true);
        } else {
            setIsBreakGlassDisabled(false);
        }

        if (!jitConfig?.isJitConfigSet) {
            return setJitStatus(BADGE_OPTIONS.MISSING_CONFIG);
        }
        if (isBreakGlass) {
            return setJitStatus(BADGE_OPTIONS.BREAK_GLASS);
        }

        if (
            (originalJitPolicy && originalJitPolicy?.protected !== editedJitPolicy?.protected) ||
            (!originalJitPolicy && editedJitPolicy?.protected)
        ) {
            setJitStatus(BADGE_OPTIONS.EDIT);
        } else if (!originalJitPolicy || !originalJitPolicy?.protected) {
            setJitStatus(BADGE_OPTIONS.NOT_PROTECTED);
        } else {
            setJitStatus(BADGE_OPTIONS.PROTECTED);
        }
    }, [originalJitPolicy, editedJitPolicy, isBreakGlass, jitConfig]);

    useEffect(() => {
        if (
            (originalFencingPolicy && originalFencingPolicy?.protected !== editedFencingPolicy?.protected) ||
            (!originalFencingPolicy && editedFencingPolicy?.protected)
        ) {
            setFencingStatus(BADGE_OPTIONS.EDIT);
        } else if (!originalFencingPolicy || !originalFencingPolicy?.protected) {
            setFencingStatus(BADGE_OPTIONS.NOT_PROTECTED);
        } else {
            setFencingStatus(BADGE_OPTIONS.PROTECTED);
        }
    }, [originalFencingPolicy, editedFencingPolicy]);

    useEffect(() => {
        const fetchJitConfig = async () => {
            try {
                const response = await httpGet(`/jit/${account.guid}`);
                if (response?.data) {
                    setIsBreakGlass(response.data.isBreakGlass);
                    if (response.data.policy) {
                        setOriginalJitPolicy(response.data.policy);
                        setEditedJitPolicy(response.data.policy);
                    }
                }
            } catch (error) {
                console.error('failed to get jit conf', error);
            }
        };
        fetchJitConfig();
    }, [account.guid]);

    const canSaveFencing = useCallback(() => {
        if (!editedFencingPolicy) return false;
        return JSON.stringify(editedFencingPolicy) !== JSON.stringify(originalFencingPolicy);
    }, [editedFencingPolicy, originalFencingPolicy]);

    const isValidMFA = useCallback(() => {
        return (
            editedJitPolicy?.mfaType === JIT_MFA_TYPES.NONE.value ||
            (editedJitPolicy?.mfaType === JIT_MFA_TYPES.MFA.value && editedJitPolicy?.tokens.length > 0) ||
            (editedJitPolicy?.mfaType === JIT_MFA_TYPES.BRIDGE.value &&
                editedJitPolicy?.vendorId !== null &&
                editedJitPolicy?.appId !== null)
        );
    }, [editedJitPolicy]);

    const isValidEnableTime = useCallback(() => {
        return (
            (editedJitPolicy?.disableAfter >= JIT_DEFAULT_MIN_ENABLE_HOURS &&
                editedJitPolicy?.disableAfter <= jitConfig?.maxEnableHours) ||
            JIT_DEFAULT_MAX_ENABLE_HOURS
        );
    }, [editedJitPolicy, jitConfig]);

    const canSaveJit = useCallback(() => {
        if (!editedJitPolicy) return false;
        return (
            isValidMFA() && isValidEnableTime() && JSON.stringify(editedJitPolicy) !== JSON.stringify(originalJitPolicy)
        );
    }, [editedJitPolicy, originalJitPolicy, isValidMFA, isValidEnableTime]);

    const save = useCallback(async () => {
        try {
            if (canSaveJit()) {
                if (!originalJitPolicy) {
                    await httpPost(`/jit/${account.guid}`, {
                        policy: { ...editedJitPolicy }
                    });
                } else {
                    await httpPut(`/jit/${account.guid}`, {
                        policy: { ...editedJitPolicy }
                    });
                }
            }
        } catch (e) {
            console.error('Failed to save JIT policy for guid ' + account.guid, e);
            onError('Failed to save JIT policy');
            onClose();
            return;
        }
        try {
            if (canSaveFencing()) {
                if (!originalFencingPolicy) {
                    await httpPost(`/fencing/${account.guid}`, {
                        policy: formatFencingPolicyForSave(editedFencingPolicy)
                    });
                } else {
                    await httpPut(`/fencing/${account.guid}`, {
                        policy: formatFencingPolicyForSave(editedFencingPolicy)
                    });
                }
            }
        } catch (e) {
            console.error('Failed to save virtual fencing policy for guid ' + account.guid, e);
            onError('Failed to save virtual fencing policy');
            onClose();
            return;
        }

        onSave(account, editedJitPolicy?.protected, editedFencingPolicy?.protected);
        onClose();
    }, [
        account,
        editedJitPolicy,
        editedFencingPolicy,
        originalJitPolicy,
        originalFencingPolicy,
        onClose,
        onError,
        onSave,
        canSaveJit,
        canSaveFencing
    ]);

    const jitPolicy = useMemo(
        () => (
            <JitPolicy
                isBreakGlassDisabled={isBreakGlassDisabled}
                editedPolicy={editedJitPolicy}
                setEditedPolicy={setEditedJitPolicy}
                account={account}
                isEditor={isEditor}
            />
        ),
        [account, editedJitPolicy, isBreakGlassDisabled, isEditor]
    );

    const fencingPolicy = useMemo(
        () => (
            <FencingPolicy
                editedPolicy={editedFencingPolicy}
                setOriginalPolicy={setOriginalFencingPolicy}
                setEditedPolicy={setEditedFencingPolicy}
                account={account}
                isEditor={isEditor}
            />
        ),
        [account, editedFencingPolicy, isEditor]
    );

    const accordionSummaryProps = useMemo(() => {
        expandIcon: <Icon icon={Icons.ChevronDown} />;
    }, []);

    const canSavePaPolicies = useCallback(() => {
        const isJitChanged = JSON.stringify(editedJitPolicy) !== JSON.stringify(originalJitPolicy);
        const isFencingChanged = JSON.stringify(editedFencingPolicy) !== JSON.stringify(originalFencingPolicy);
        return (
            (!editedJitPolicy?.protected || canSaveJit() || !isJitChanged) &&
            (!editedFencingPolicy?.protected || canSaveFencing() || !isFencingChanged) &&
            (isJitChanged || isFencingChanged) &&
            isEditor
        );
    }, [editedJitPolicy, editedFencingPolicy, originalJitPolicy, originalFencingPolicy, canSaveJit, canSaveFencing]);

    return (
        <div>
            <Drawer
                className="max-w-[720px] w-[720px]"
                open={open}
                headerTitle={account.name}
                onClose={(e, reason) => reason !== 'backdropClick' && onClose(e, reason)}
                onAction={save}
                footerActionProps={{ disabled: !canSavePaPolicies() }}
                footerActionTitle="Save"
                footerCloseTitle="Cancel">
                <div className="text-13 w-[720px] h-full flex flex-col gap-4">
                    <Accordion
                        sx={{
                            '&:before': {
                                display: 'none'
                            }
                        }}
                        disableGutters
                        elevation={0}
                        defaultExpanded={true}
                        className="shadow-none border rounded-lg !border-sf-gray-100"
                        summary={
                            <div className="h-[32px] flex items-center pr-2 font-medium">
                                <span className="pr-4 font-sf-primary">Virtual Fencing Policy</span>
                                {fencingStatus && (
                                    <StatusBadge
                                        content={fencingStatus.title}
                                        variant={fencingStatus.theme}></StatusBadge>
                                )}
                            </div>
                        }
                        accordionSummaryProps={accordionSummaryProps}
                        children={fencingPolicy}></Accordion>

                    <div className="pb-4">
                        <Accordion
                            sx={{
                                '&:before': {
                                    display: 'none'
                                }
                            }}
                            disableGutters
                            elevation={0}
                            defaultExpanded={true}
                            className="shadow-none border rounded-lg !border-sf-gray-100"
                            summary={
                                <div className="h-[32px] items-center flex pr-2 font-medium">
                                    <span className="pr-4 font-sf-primary">Just In Time (JIT) Policy</span>

                                    {jitStatus &&
                                        (jitStatus.tooltipProps ? (
                                            <div>
                                                <Tooltip {...jitStatus.tooltipProps}>
                                                    <StatusBadge content={jitStatus.title} variant={jitStatus.theme} />
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            <StatusBadge content={jitStatus.title} variant={jitStatus.theme} />
                                        ))}
                                </div>
                            }
                            accordionSummaryProps={accordionSummaryProps}
                            children={jitPolicy}
                        />
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default PaPolicies;
